import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { ArrowLeft, Image as ImageIcon } from "lucide-react";

export default function AlbumDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/public/albums/${id}`).then((r) => setAlbum(r.data)).catch(() => setAlbum(false));
  }, [id]);

  if (album === false)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="text-2xl font-semibold">{t("gallery.albumNotFound")}</h1>
        <Link to="/galerie" className="text-bordeaux hover:underline mt-4 inline-block">
          {t("gallery.backAll")}
        </Link>
      </div>
    );
  if (!album) return <div className="max-w-3xl mx-auto px-6 py-32 text-slate-500">{t("common.loading")}</div>;

  return (
    <div data-testid="album-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20">
      <Link to="/galerie" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux mb-8">
        <ArrowLeft size={14} /> {t("gallery.backAll")}
      </Link>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">{album.title}</h1>
      {album.description && <p className="mt-4 text-lg text-slate-600 max-w-2xl">{album.description}</p>}

      {album.images.length === 0 ? (
        <div className="mt-12 border border-dashed border-slate-200 rounded-sm py-20 text-center text-slate-500">
          <ImageIcon className="mx-auto mb-3" size={22} />
          {t("gallery.emptyAlbum")}
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {album.images.map((img) => (
            <button
              key={img.id}
              onClick={() => setActive(img)}
              data-testid={`album-image-${img.id}`}
              className="group text-left bg-white rounded-sm overflow-hidden border border-slate-200 hover:border-bordeaux transition-colors"
            >
              <div className="aspect-square bg-beige-light overflow-hidden">
                <img
                  src={fileUrl(img.path)}
                  alt={img.caption || album.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              {img.caption && (
                <div className="p-3 text-xs text-slate-600 leading-relaxed line-clamp-2">
                  {img.caption}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {active && (
        <div
          onClick={() => setActive(null)}
          className="fixed inset-0 bg-slate-900/90 z-50 flex flex-col items-center justify-center p-6 cursor-zoom-out"
          data-testid="lightbox"
        >
          <img
            src={fileUrl(active.path)}
            alt={active.caption || ""}
            className="max-h-[80vh] max-w-full object-contain"
          />
          {active.caption && (
            <div
              data-testid="lightbox-caption"
              className="mt-6 max-w-2xl text-center text-sm text-white/90 leading-relaxed whitespace-pre-line"
            >
              {active.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
